import React, { useState, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CameraScanner = ({ onPackageAdded, managementButtons }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [lastCapturedPackageId, setLastCapturedPackageId] = useState(null);
  const [additionalPhotos, setAdditionalPhotos] = useState([]);
  const [flashEnabled, setFlashEnabled] = useState(false);
  const [currentStream, setCurrentStream] = useState(null);

  const startCamera = useCallback(async () => {
    try {
      // Check if mediaDevices is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        toast.error('Camera not supported on this device or browser', {
          duration: 5000
        });
        return;
      }

      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { 
          width: { ideal: 1280 }, 
          height: { ideal: 720 },
          facingMode: 'environment' // Use back camera on mobile
        } 
      });
      
      // Store stream for flash control
      setCurrentStream(stream);
      
      // Try to enable flash if requested
      if (flashEnabled && stream.getVideoTracks().length > 0) {
        const track = stream.getVideoTracks()[0];
        const capabilities = track.getCapabilities();
        
        if (capabilities.torch) {
          try {
            await track.applyConstraints({
              advanced: [{ torch: true }]
            });
          } catch (err) {
            console.log('Flash not supported:', err);
          }
        }
      }
      
      // Set streaming state first so video element renders in DOM
      setIsStreaming(true);
      
      // Wait for next render cycle to ensure video element exists
      setTimeout(async () => {
        if (videoRef.current) {
          try {
            videoRef.current.srcObject = stream;
            
            // Explicitly play the video stream
            await videoRef.current.play();
            toast.success('Camera started successfully');
          } catch (playError) {
            console.error('Error playing video:', playError);
            toast.error('Camera started but video failed to play. Please try again.');
            // Stop the stream if play fails
            stream.getTracks().forEach(track => track.stop());
            setIsStreaming(false);
          }
        } else {
          console.error('Video ref still not available');
          toast.error('Camera initialization failed. Please try again.');
          stream.getTracks().forEach(track => track.stop());
          setIsStreaming(false);
        }
      }, 100);
    } catch (error) {
      console.error('Error accessing camera:', error);
      
      // Provide specific error messages based on error type
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        toast.error(
          <div className="text-left">
            <div className="font-bold mb-2">📷 Camera Access Required</div>
            <div className="text-sm space-y-1">
              <p><strong>How to Enable:</strong></p>
              <p>1. Click the 🔒 icon in your browser's address bar</p>
              <p>2. Find "Camera" in permissions</p>
              <p>3. Change to "Allow"</p>
              <p>4. Refresh this page</p>
            </div>
          </div>,
          {
            duration: 10000,
            action: {
              label: 'Got it',
              onClick: () => {}
            }
          }
        );
      } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
        toast.error('No camera found on this device', { duration: 5000 });
      } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
        toast.error('Camera is already in use by another application', { duration: 5000 });
      } else {
        toast.error(`Camera error: ${error.message}. Please check camera permissions.`, { duration: 5000 });
      }
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (videoRef.current && videoRef.current.srcObject) {
      const tracks = videoRef.current.srcObject.getTracks();
      tracks.forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
    if (currentStream) {
      currentStream.getTracks().forEach(track => track.stop());
      setCurrentStream(null);
    }
    setIsStreaming(false);
    setCapturedImage(null);
    toast.info('Camera stopped');
  }, [currentStream]);

  const toggleFlash = async () => {
    const newFlashState = !flashEnabled;
    setFlashEnabled(newFlashState);
    
    if (currentStream && currentStream.getVideoTracks().length > 0) {
      const track = currentStream.getVideoTracks()[0];
      const capabilities = track.getCapabilities();
      
      if (capabilities.torch) {
        try {
          await track.applyConstraints({
            advanced: [{ torch: newFlashState }]
          });
          toast.success(newFlashState ? '⚡ Flash ON' : '🌙 Flash OFF', { duration: 1500 });
        } catch (err) {
          toast.error('Flash not supported on this device');
        }
      } else {
        toast.error('Flash not supported on this device');
      }
    }
  };

  const captureImage = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const video = videoRef.current;
    const context = canvas.getContext('2d');

    // Set canvas size to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw current video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert canvas to data URL
    const imageDataUrl = canvas.toDataURL('image/jpeg', 0.8);
    setCapturedImage(imageDataUrl);
    
    toast.success('Image captured! Processing...');
    processImage(imageDataUrl);
  }, []);

  const processImage = async (imageDataUrl) => {
    setIsProcessing(true);
    
    try {
      // Use enhanced OCR endpoint
      const formData = new FormData();
      formData.append('image_data', imageDataUrl);
      formData.append('auto_assign_box', 'true');
      
      const response = await axios.post(`${API}/extract-label-enhanced`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.needs_retake) {
        toast.error('❌ Could not read label. Please retake the picture with better lighting/angle.', {
          duration: 5000
        });
        setCapturedImage(null);
        setIsProcessing(false);
        return;
      }

      if (response.data.success && response.data.data) {
        const { 
          recipient_name, 
          tracking_numbers, 
          carrier, 
          needs_review,
          phones,
          emails,
          raw_text
        } = response.data.data;
        const boxNumber = response.data.box_number;
        
        if (needs_review) {
          toast.warning('⚠️ Some fields need review. Please verify the information.', {
            duration: 5000
          });
        }

        if (recipient_name || (tracking_numbers && tracking_numbers.length > 0)) {
          // Create package with enhanced data
          const packageData = {
            recipient_name: recipient_name || 'Unknown Recipient',
            carrier: carrier || 'Unknown Carrier',
            box_number: boxNumber,
            tracking_numbers: tracking_numbers || [],
            phone: phones && phones.length > 0 ? phones[0] : null,
            email: emails && emails.length > 0 ? emails[0] : null,
            notes: `Auto-scanned - Box #${boxNumber}`,
            ocr_data: response.data.data,
            raw_data: raw_text || null,  // Store complete raw OCR text
            needs_review: needs_review,
            captured_image: capturedImage  // Include the captured photo
          };
          
          console.log('Sending package data with image:', {
            ...packageData,
            captured_image: capturedImage ? `${capturedImage.substring(0, 50)}... (${capturedImage.length} chars)` : 'null'
          });

          const createResponse = await axios.post(`${API}/packages`, packageData);
          console.log('Package created response:', createResponse.data);
          
          if (createResponse.data) {
            onPackageAdded(createResponse.data);
            
            // Save the package ID for additional photos
            setLastCapturedPackageId(createResponse.data.id);
            setAdditionalPhotos([]);
            
            // Show success with box number prominently
            toast.success(
              <div>
                <div className="text-lg font-bold">📦 Package Added!</div>
                <div className="text-2xl font-bold text-orange-600">Box #{boxNumber}</div>
                <div>{recipient_name || 'Unknown'}</div>
                {tracking_numbers && tracking_numbers.length > 0 && (
                  <div className="text-sm">{tracking_numbers.join(', ')}</div>
                )}
                <div className="text-xs mt-2 text-gray-600">You can add more photos if needed</div>
              </div>,
              { duration: 6000 }
            );
            
            setCapturedImage(null);
          }
        } else {
          toast.warning('❌ No recipient or tracking found. Please retake or add manually.', {
            duration: 5000
          });
          setCapturedImage(null);
        }
      } else {
        toast.error('Could not extract information. Please retake the picture.', {
          duration: 5000
        });
        setCapturedImage(null);
      }
    } catch (error) {
      console.error('Error processing image:', error);
      toast.error('Failed to process image. Please try again.', { duration: 5000 });
      setCapturedImage(null);
    } finally {
      setIsProcessing(false);
    }
  };

  const retakePhoto = () => {
    setCapturedImage(null);
  };

  const captureAdditionalPhoto = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const video = videoRef.current;
    const context = canvas.getContext('2d');

    // Set canvas size to match video
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    // Draw current video frame to canvas
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert canvas to data URL
    const imageDataUrl = canvas.toDataURL('image/jpeg', 0.8);
    
    // Add to additional photos array
    setAdditionalPhotos(prev => [...prev, imageDataUrl]);
    
    toast.success(
      <div>
        <div className="text-lg font-bold">📸 Additional Photo Captured!</div>
        <div className="text-sm">Photo #{additionalPhotos.length + 1} added to last entry</div>
      </div>,
      { duration: 3000 }
    );
    
    // Could also update backend here to attach photo to package
    // For now just storing locally
  }, [additionalPhotos.length]);

  return (
    <>
      {/* Main Card Container - Only shown when camera is NOT active */}
      {!isStreaming && !capturedImage && (
        <div className="relative bg-gradient-to-br from-indigo-50 to-purple-50 rounded-3xl p-6 shadow-xl border border-white backdrop-blur-sm">
          {/* Horizontal Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center">
              <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-2xl flex items-center justify-center mr-4 transform rotate-3 shadow-lg">
                <i className="fas fa-camera text-white text-xl"></i>
              </div>
              <div className="text-center">
                <h2 className="text-xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  AI Label Scanner
                </h2>
                <p className="text-gray-600 text-sm">Advanced OCR Technology</p>
              </div>
            </div>
            
            {/* Management Buttons on Right */}
            <div className="flex items-center space-x-3">
              {managementButtons}
              
              {/* Additional Photos Counter */}
              {lastCapturedPackageId && additionalPhotos.length > 0 && (
                <div className="bg-gradient-to-r from-orange-500 to-yellow-500 text-white px-4 py-2 rounded-full flex items-center space-x-2 shadow-lg">
                  <i className="fas fa-images"></i>
                  <span className="font-bold">{additionalPhotos.length} Extra Photo{additionalPhotos.length > 1 ? 's' : ''}</span>
                </div>
              )}
            </div>
          </div>
          
          <div className="space-y-4">
            {/* Camera Controls */}
            <div className="flex justify-center">
              <button
                onClick={startCamera}
                className="w-48 h-48 bg-gradient-to-br from-blue-500 via-purple-500 to-indigo-600 hover:from-blue-600 hover:via-purple-600 hover:to-indigo-700 text-white font-bold text-xl rounded-3xl shadow-2xl hover:shadow-blue-500/50 transform hover:scale-105 transition-all duration-300 flex flex-col items-center justify-center space-y-3"
                data-testid="start-camera-btn"
              >
                <i className="fas fa-camera text-5xl"></i>
                <span>Start Camera</span>
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Video Stream - FULL SCREEN OVERLAY */}
      {isStreaming && (
          <div className="fixed inset-0 bg-black z-[9999]" style={{ width: '100vw', height: '100vh', top: 0, left: 0 }}>
            {/* Video element */}
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-full object-cover"
              style={{ 
                width: '100%', 
                height: '100%',
                objectFit: 'cover'
              }}
              data-testid="camera-video"
            />
            
            {/* Bottom Controls - Add to Last, Capture (center), Flash */}
            {!capturedImage && !isProcessing && (
              <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 flex items-center space-x-4 z-50">
                {/* Add to Last Entry Button - LEFT (appears after first scan) */}
                {lastCapturedPackageId && (
                  <button
                    onClick={() => captureAdditionalPhoto()}
                    className="w-20 h-20 bg-gradient-to-r from-orange-500 to-yellow-600 rounded-full flex items-center justify-center shadow-2xl hover:shadow-orange-500/50 transform hover:scale-110 transition-all duration-300 border-4 border-white"
                    data-testid="add-to-last-btn"
                    title="Add photo to last entry"
                  >
                    <i className="fas fa-plus text-white text-3xl"></i>
                  </button>
                )}
                
                {/* Capture Button - CENTER (always visible) */}
                <button
                  onClick={captureImage}
                  className="w-20 h-20 bg-gradient-to-r from-blue-500 to-purple-600 rounded-full flex items-center justify-center shadow-2xl hover:shadow-blue-500/50 transform hover:scale-110 transition-all duration-300 border-4 border-white"
                  data-testid="capture-circular-btn"
                  title="Capture photo"
                >
                  <i className="fas fa-camera text-white text-3xl"></i>
                </button>
                
                {/* Flash Toggle Button - RIGHT (always visible) */}
                <button
                  onClick={toggleFlash}
                  className={`w-20 h-20 rounded-full flex items-center justify-center shadow-2xl transform hover:scale-110 transition-all duration-300 border-4 border-white ${
                    flashEnabled 
                      ? 'bg-gradient-to-r from-yellow-400 to-orange-500 hover:shadow-yellow-500/50' 
                      : 'bg-gradient-to-r from-gray-600 to-gray-800 hover:shadow-gray-500/50'
                  }`}
                  data-testid="flash-toggle-btn"
                  title={flashEnabled ? 'Flash ON' : 'Flash OFF'}
                >
                  <i className={`fas ${flashEnabled ? 'fa-bolt' : 'fa-bolt-slash'} text-white text-3xl`}></i>
                </button>
              </div>
            )}
            
            {/* Top Right - Close Button */}
            <div className="absolute top-4 right-4 z-50">
              <button
                onClick={stopCamera}
                className="px-4 py-2 bg-red-500 text-white font-medium rounded-full flex items-center space-x-2 shadow-lg hover:bg-red-600 transition-all duration-200"
                data-testid="stop-camera-overlay-btn"
              >
                <i className="fas fa-times"></i>
                <span>Close</span>
              </button>
            </div>
          </div>
        )}

        {/* Captured Image Preview - FULL SCREEN OVERLAY */}
        {capturedImage && (
          <div className="fixed inset-0 bg-black bg-opacity-95 z-[9999] flex items-center justify-center p-4" style={{ width: '100vw', height: '100vh', top: 0, left: 0 }}>
            <div className="max-w-4xl w-full space-y-4">
              <div className="text-center">
                <img
                  src={capturedImage}
                  alt="Captured label"
                  className="max-w-full max-h-[70vh] mx-auto rounded-lg border-2 border-gray-300 shadow-2xl"
                  data-testid="captured-image"
                />
              </div>
            
            <div className="flex justify-center space-x-3">
              {!isProcessing && (
                <button
                  onClick={retakePhoto}
                  className="btn-primary px-4 py-2 rounded-lg font-medium flex items-center space-x-2"
                  data-testid="retake-photo-btn"
                >
                  <i className="fas fa-redo"></i>
                  <span>Retake Photo</span>
                </button>
              )}
              
              {isProcessing && (
                <div className="flex items-center space-x-2 text-blue-600">
                  <div className="loading-spinner"></div>
                  <span>Extracting label information...</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Hidden canvas for image capture */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />
    </>
  );
};

export default CameraScanner;